import { Action, AppState, initialExperimentListState, initialMicroLearningListState } from './AppProvider'

// Define the reducer function
export const appStateReducer = (state: AppState, action: Action): AppState => {
  switch (action.type) {
    case 'UPDATE_CURRENT_CHAT':
      if (action.payload) {
        action.payload.messages = action.payload.messages.sort(
          (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
        )
      }
      return { ...state, currentChat: action.payload }
    case 'UPDATE_CHAT_HISTORY_LOADING_STATE':
      return { ...state, chatHistoryLoadingState: action.payload }
    case 'UPDATE_CHAT_HISTORY':
      if (!state.chatHistory || !state.currentChat) {
        return state
      }
      const conversationIndex = state.chatHistory.findIndex(conv => conv.id === action.payload.id)
      if (conversationIndex !== -1) {
        const updatedChatHistory = [...state.chatHistory]
        updatedChatHistory[conversationIndex] = state.currentChat
        return { ...state, chatHistory: updatedChatHistory }
      } else {
        return { ...state, chatHistory: [...state.chatHistory, action.payload] }
      }
    case 'UPDATE_CHAT_TITLE':
      if (!state.chatHistory) {
        return { ...state, chatHistory: [] }
      }
      const updatedChats = state.chatHistory.map(chat => {
        if (chat.id === action.payload.id) {
          if (state.currentChat?.id === action.payload.id) {
            state.currentChat.title = action.payload.title
          }
          //TODO: make api call to save new title to DB
          return { ...chat, title: action.payload.title }
        }
        return chat
      })
      return { ...state, chatHistory: updatedChats }
    case 'DELETE_CHAT_ENTRY':
      if (!state.chatHistory) {
        return { ...state, chatHistory: [] }
      }
      const filteredChat = state.chatHistory.filter(chat => chat.id !== action.payload)
      state.currentChat = null
      //TODO: make api call to delete conversation from DB
      return { ...state, chatHistory: filteredChat }
    case 'DELETE_CHAT_HISTORY':
      //TODO: make api call to delete all conversations from DB
      return { ...state, chatHistory: [], filteredChatHistory: [], currentChat: null }
    case 'DELETE_CURRENT_CHAT_MESSAGES':
      //TODO: make api call to delete current conversation messages from DB
      if (!state.currentChat || !state.chatHistory) {
        return state
      }
      const updatedCurrentChat = {
        ...state.currentChat,
        messages: [],
      }
      return {
        ...state,
        currentChat: updatedCurrentChat,
      }
    case 'FETCH_CHAT_HISTORY':
      return { ...state, chatHistory: action.payload }
    case 'SET_COSMOSDB_STATUS':
      return { ...state, isCosmosDBAvailable: action.payload }
    case 'FETCH_FRONTEND_SETTINGS':
      return { ...state, isLoading: false, frontendSettings: action.payload }
    case 'SET_FEEDBACK_STATE':
      return {
        ...state,
        feedbackState: {
          ...state.feedbackState,
          [action.payload.answerId]: action.payload.feedback,
        },
      }
    case 'SET_ANSWER_EXEC_RESULT':
      return {
        ...state,
        answerExecResult: {
          ...state.answerExecResult,
          [action.payload.answerId]: action.payload.exec_result,
        },
      }
    case 'SET_SHOULD_VALIDATE_FORM':
      return {
        ...state,
        shouldValidateForm: action.payload,
      }
    case 'SET_IS_USER_ACTIVE':
      return {
        ...state,
        isUserActive: action.payload,
      }
    case 'SET_HOME_CHAT_MESSAGE':
      return {
        ...state,
        homeChatMessage: action.payload,
      }
    case 'SET_SNACKBAR_STATE':
      return {
        ...state,
        snackbarState: action.payload,
      }
    case 'SET_PERMISSIONS':
      return {
        ...state,
        permissions: action.payload,
      }
    case 'SET_IS_NEW_CHAT_DIALOG_OPEN':
      return {
        ...state,
        isNewChatDialogOpen: action.payload,
      }
    case 'SET_EXPERIMENT_LIST_STATE':
      return {
        ...state,
        experimentListState: action.payload,
      }
    case 'SET_EXPERIMENT_LIST_SORT_BY':
      return {
        ...state,
        experimentListState: {
          ...state.experimentListState,
          sortBy: action.payload,
        },
      }
    case 'SET_EXPERIMENT_LIST_SORT_ORDER':
      return {
        ...state,
        experimentListState: {
          ...state.experimentListState,
          sortOrder: action.payload,
        },
      }
    case 'SET_EXPERIMENT_LIST_FILTERS':
      return {
        ...state,
        experimentListState: {
          ...state.experimentListState,
          filters: action.payload,
        },
      }
    case 'SET_EXPERIMENT_LIST_SEARCH_QUERY':
      return {
        ...state,
        experimentListState: { ...state.experimentListState, searchQuery: action.payload },
      }
    case 'RESET_EXPERIMENT_LIST_STATE':
      return {
        ...state,
        experimentListState: initialExperimentListState,
      }
    case 'SET_MICRO_LEARNING_LIST_STATE':
      return {
        ...state,
        microLearningListState: action.payload,
      }
    case 'RESET_MICRO_LEARNING_LIST_STATE':
      return {
        ...state,
        microLearningListState: initialMicroLearningListState,
      }
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        microLearningListState: action.payload ? state.microLearningListState : initialMicroLearningListState,
      }
    case 'SET_CHANGE_ROLE_REQUEST':
      return {
        ...state,
        changeRoleRequest: action.payload,
      }
    case 'SET_HAS_PENDING_ROLE_CHANGE_REQUEST':
      return {
        ...state,
        hasPendingRoleChangeRequest: action.payload,
      }
    case 'SET_ROLE_CHANGE_REQUEST_FILTER_STATE':
      return {
        ...state,
        roleChangeRequestFilterState: action.payload,
      }
    case 'SET_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload,
      }
    case 'MARK_ALL_NOTIFICATIONS_AS_READ':
      return {
        ...state,
        notifications:
          state.notifications?.map(notification => ({
            ...notification,
            is_read: true,
          })) || [],
      }
    case 'DELETE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications?.filter(notification => notification.id !== action.payload) || [],
      }
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...(state.notifications || [])],
      }
    case 'SET_USER_FILTER':
      return {
        ...state,
        userFilter: action.payload,
      }
    case 'SET_USER_HISTORY_FILTER':
      return {
        ...state,
        userHistoryFilter: action.payload,
      }
    case 'SET_LOOKUPS':
      return {
        ...state,
        lookups: action.payload,
      }
    case 'SET_IS_REQUEST_ACCESS_DIALOG_OPEN':
      return {
        ...state,
        isRequestAccessDialogOpen: action.payload,
      }
    default:
      return state
  }
}
